import React, { useState, useEffect } from "react";
import { Row, Col, Spin, Form, Card, InputNumber, Select } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { connect } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

import MainLayout from "../../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../../components/layouts/enhancement/TopContent";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import cnt from "../../../../services/definedConstant";
import { rightAction } from "../../../../redux/actions";
import {
  labelConfig,
  staticConfig,
  apiConfig,
} from "../../treasuryContractBooking.config";
import { BOM } from "../../../../services/definedMethod";

const RecordAuctionResults = (props) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [auctionLineOptions, setAuctionLineOptions] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const auctionId = location.state;
  const { userData } = props;
  const { session } = userData || {};

  useEffect(() => {
    if (auctionId) fetchLinesTemplate(auctionId);
  }, [auctionId]);

  const fetchLinesTemplate = (id) => {
    setIsLoading(true);
    const body = { session };
    const url = `${apiConfig.GetAuctions}/auctions/${id}/bids/template`;

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.errors?.[0]?.defaultUserMessage ||
            err?.defaultUserMessage ||
            "Failed to load auction lines.",
        );
        setIsLoading(false);
        return;
      }

      // res.lines shape: [{ auctionLineId, tenorDays, maturityDate, offerAmount, ... }]
      const lines = (res?.lines || []).map((l) => ({
        label: `${l.tenorDays}-Day Line (ID: ${l.auctionLineId})`,
        value: l.auctionLineId,
      }));
      setAuctionLineOptions(lines);
      setIsLoading(false);
    });
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const onFinish = (values) => {
    const body = {
      method: "POST",
      session,
      auctionLineId: values.auctionLineId,
      stopRate: values.stopRate,
      totalOffered: values.totalOffered,
      totalBid: values.totalBid,
      totalAllotted: values.totalAllotted,
      dateFormat: "yyyy-MM-dd",
      locale: "en",
    };

    const url = `${apiConfig.GetAuctions}/auctions/${auctionId}/results`;

    setIsLoading(true);
    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.errors?.[0]?.defaultUserMessage ||
            err?.defaultUserMessage ||
            "Failed to record auction results.",
        );
        setIsLoading(false);
        return;
      }
      BOM.AlertMsg(cnt.SUCCESS, "Auction results recorded successfully.");
      setIsLoading(false);
      navigate(-1);
    });
  };

  const Tip = ({ text, tip }) => (
    <span>
      {text}
      <Tooltip title={tip}>
        <InfoCircleOutlined style={{ marginLeft: 4, color: "#667085" }} />
      </Tooltip>
    </span>
  );

  return (
    <MainLayout pageItems={staticConfig.newAuction}>
      <TopContent pageTitle={labelConfig.auction} />

      <Spin spinning={isLoading} size="large">
        <div style={{ padding: "24px" }}>
          <Card
            style={{
              boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
              borderRadius: 20,
            }}
          >
            <h3 style={{ marginBottom: 24, fontWeight: 600 }}>
              Record Auction Results
            </h3>

            <Form layout="vertical" form={form} onFinish={onFinish}>
              {/* Auction Line — LOV from template */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Auction Line"
                        tip="The auction line these results are being recorded against."
                      />
                    }
                    name="auctionLineId"
                    rules={[
                      { required: true, message: "Auction line is required" },
                    ]}
                  >
                    <Select
                      style={{ height: 44 }}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={auctionLineOptions}
                      placeholder="Select Auction Line"
                      showSearch
                      optionFilterProp="label"
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Stop Rate (%)"
                        tip="The marginal / stop-out rate at which bids were accepted."
                      />
                    }
                    name="stopRate"
                    rules={[
                      { required: true, message: "Stop rate is required" },
                      {
                        type: "number",
                        min: 0,
                        message: "Stop rate must be 0 or greater",
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%", height: 44 }}
                      placeholder="e.g. 12.85"
                      precision={2}
                      min={0}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Total Offered & Total Bid */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Total Offered"
                        tip="The total face value amount offered at this auction line."
                      />
                    }
                    name="totalOffered"
                    rules={[
                      { required: true, message: "Total offered is required" },
                      {
                        type: "number",
                        min: 1,
                        message: "Amount must be greater than 0",
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%", height: 44 }}
                      placeholder="e.g. 1,000,000.00"
                      formatter={(v) =>
                        v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                      }
                      parser={(v) => v?.replace(/,/g, "")}
                      precision={2}
                      min={0}
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Total Bid"
                        tip="The total amount bid by all participants at this auction line."
                      />
                    }
                    name="totalBid"
                    rules={[
                      { required: true, message: "Total bid is required" },
                      {
                        type: "number",
                        min: 1,
                        message: "Amount must be greater than 0",
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%", height: 44 }}
                      placeholder="e.g. 25,000,000.00"
                      formatter={(v) =>
                        v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                      }
                      parser={(v) => v?.replace(/,/g, "")}
                      precision={2}
                      min={0}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Total Allotted */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Total Allotted"
                        tip="The total amount allotted to successful bidders."
                      />
                    }
                    name="totalAllotted"
                    rules={[
                      { required: true, message: "Total allotted is required" },
                      {
                        type: "number",
                        min: 1,
                        message: "Amount must be greater than 0",
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%", height: 44 }}
                      placeholder="e.g. 10,000,000.00"
                      formatter={(v) =>
                        v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                      }
                      parser={(v) => v?.replace(/,/g, "")}
                      precision={2}
                      min={0}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <div
                style={{
                  borderTop: "1px solid #EAECF0",
                  margin: "24px 0 20px",
                }}
              />

              <Row justify="end">
                <Col>
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    style={{
                      height: 44,
                      padding: "0 24px",
                      borderRadius: 8,
                      border: "1px solid #D0D5DD",
                      background: "#fff",
                      color: "#344054",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                      marginRight: 12,
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => form.submit()}
                    style={{
                      height: 44,
                      padding: "0 24px",
                      borderRadius: 8,
                      border: "1px solid #BA24D5",
                      background: "#BA24D5",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                    }}
                  >
                    Submit
                  </button>
                </Col>
              </Row>

              <button type="submit" hidden />
            </Form>
          </Card>
        </div>
      </Spin>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  rightBtn: state.RightSideBtn,
  userData: state.AuthenticationData.userData,
});

export default connect(mapStateToProps, { rightAction })(RecordAuctionResults);
